<script>
import firebase from "firebase";
import { guestQAMenuLabel } from "@/utils/guestQA";

export default {
  data() {
    return {
      pendingTemplateEdit: null,
      pdfDownloadInFlight: false,
      pdfDownloadAbortController: null,
      confirmDelete: {
        show: true,
        title: "Are You Sure?",
        message: "are you sure you want to delete this property?",
        width: 350,
        persistent: false,
        loading: false,
        actions: [
          { label: "yes delete", clickEmit: "deleteProp", color: "error" },
          { type: "spacer" },
          { label: "cancel", clickEmit: "cancel", color: "success" },
        ],
      },
      confirmEditDefaultTemplate: {
        show: true,
        title: "Edit Default Property Template?",
        message:
          "You are about to edit the global default property template. Changes here affect the starting content for all newly created properties. Are you sure you want to continue?",
        width: 480,
        persistent: true,
        actions: [
          { label: "cancel", clickEmit: "cancel", plain: true },
          { type: "spacer" },
          {
            label: "Continue to Editor",
            clickEmit: "confirmEditDefaultTemplate",
            color: "warning",
          },
        ],
      },
    };
  },
  created() {},
  computed: {
    propertyAnalyticsMenuItems() {
      if (!this.loggedIn) {
        return [];
      }
      return [
        {
          type: "link",
          label: "Analytics",
          icon: "mdi-chart-box-outline",
          emitClick: "showAnalytics",
          toolTip: "Guest Analytics",
        },
      ];
    },
    propertyGuestQAMenuItems() {
      if (!this.loggedIn) {
        return [];
      }
      return [
        {
          type: "link",
          label: "Guest Q&A",
          contextLabel: guestQAMenuLabel,
          icon: "mdi-comment-question-outline",
          emitClick: "showGuestQABuilder",
          toolTip:
            "Unanswered questions, Q&A page, and email alerts (Pro, Premium, Guest Ask)",
        },
      ];
    },
    propertyPdfMenuItems() {
      if (!this.loggedIn) {
        return [];
      }
      return [
        {
          type: "link",
          label: "Download guide",
          icon: "mdi-printer-outline",
          emitClick: "downloadPropertyPdf",
          toolTip: "Download PDF or Word (.docx) property guide",
        },
      ];
    },
  },
  methods: {
    propertyGuidePdfEndpoint() {
      const projectId =
        firebase.app().options.projectId ||
        (firebase.app().options.authDomain || "").split(".")[0] ||
        "everhostio";
      return `https://us-central1-${projectId}.cloudfunctions.net/generatePropertyGuidePdf`;
    },
    sanitizeGuideFilename(name, extension) {
      const cleaned = String(name || "property-guide")
        .replace(/[^\w\s-]+/g, "")
        .trim()
        .replace(/\s+/g, "-")
        .slice(0, 80);
      const ext = extension === "docx" || extension === "doc" ? "docx" : "pdf";
      return `${cleaned || "property-guide"}-property-guide.${ext}`;
    },
    showPropertyGuideDownloadOptions(property) {
      this.message = {
        show: true,
        title: "Download guide",
        titleIcon: "mdi-printer-outline",
        message: "Choose PDF for printing, or Word (.docx) for editing.",
        prop: property,
        width: 420,
        actions: [
          {
            label: "PDF",
            clickEmit: "downloadGuidePdf",
            color: "primary",
          },
          {
            label: "Word (.docx)",
            clickEmit: "downloadGuideDoc",
          },
          { type: "spacer" },
          { label: "cancel", clickEmit: "cancel", plain: true },
        ],
      };
    },
    downloadPropertyGuidePdf(property) {
      this.showPropertyGuideDownloadOptions(property);
    },
    downloadGuideAsPdf() {
      this.downloadPropertyGuideFile(this.message?.prop, "pdf");
    },
    downloadGuideAsDoc() {
      this.downloadPropertyGuideFile(this.message?.prop, "docx");
    },
    async downloadPropertyGuideFile(property, format = "pdf") {
      const propertyId =
        property?.propertyId || property?.key || property?.docId || property;
      if (!propertyId) {
        this.message = {
          show: true,
          title: "Download failed",
          message: "A valid property is required.",
          width: 360,
          actions: [{ type: "spacer" }, { label: "ok", clickEmit: "cancel" }],
        };
        return;
      }

      if (this.pdfDownloadInFlight) {
        return;
      }

      const user = firebase.auth().currentUser;
      if (!user) {
        this.message = {
          show: true,
          title: "Sign in required",
          message: "Please sign in to download a printable guide.",
          width: 360,
          actions: [{ type: "spacer" }, { label: "ok", clickEmit: "cancel" }],
        };
        return;
      }

      const isDoc = format === "docx" || format === "doc";
      this.pdfDownloadInFlight = true;
      this.pdfDownloadAbortController = new AbortController();
      this.message = {
        show: true,
        title: isDoc
          ? "Generating Word guide…"
          : "Generating printable guide…",
        message: isDoc
          ? "Creating a Word (.docx) copy of this property guide with images embedded."
          : "Creating a polished PDF of this property guide. This can take up to a minute.",
        width: 420,
        persistent: true,
        loading: true,
        actions: [
          { type: "spacer" },
          { label: "cancel", clickEmit: "cancelPdfDownload", plain: true },
        ],
      };

      try {
        const idToken = await user.getIdToken();
        const response = await fetch(this.propertyGuidePdfEndpoint(), {
          method: "POST",
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            propertyId: String(propertyId),
            format: isDoc ? "docx" : "pdf",
          }),
          signal: this.pdfDownloadAbortController.signal,
        });

        if (!response.ok) {
          let errorText = "Failed to generate the printable guide.";
          try {
            const payload = await response.json();
            if (payload?.error) {
              errorText = payload.error;
            }
          } catch (parseError) {
            // Keep default error text.
          }
          throw new Error(errorText);
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = objectUrl;
        anchor.download = this.sanitizeGuideFilename(
          property?.name || propertyId,
          isDoc ? "docx" : "pdf",
        );
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(objectUrl);

        this.message = {
          show: true,
          title: "Guide ready",
          titleIcon: "mdi-beta",
          message: isDoc
            ? "Your Word (.docx) property guide has been downloaded."
            : "Your printable property guide has been downloaded.",
          width: 360,
          actions: [{ type: "spacer" }, { label: "ok", clickEmit: "cancel" }],
        };
      } catch (error) {
        if (error?.name === "AbortError") {
          this.message = { show: false };
        } else {
          console.error("downloadPropertyGuideFile failed", error);
          this.message = {
            show: true,
            title: "Download failed",
            message:
              error?.message ||
              "Could not generate the printable guide. Please try again.",
            width: 400,
            actions: [{ type: "spacer" }, { label: "ok", clickEmit: "cancel" }],
          };
        }
      } finally {
        this.pdfDownloadInFlight = false;
        this.pdfDownloadAbortController = null;
      }
    },
    cancelPropertyGuidePdfDownload() {
      if (this.pdfDownloadAbortController) {
        this.pdfDownloadAbortController.abort();
      }
      this.message = { show: false };
    },
    isDefaultPropertyTemplate(prop) {
      const templateId = this.$store.getters.defaultPropertyId;
      const id = prop?.propertyId || prop?.key || prop?.docId || prop;
      return String(id) === String(templateId);
    },
    openPropertyEditor(prop) {
      const propertyId = prop?.propertyId || prop?.key || prop?.docId || prop;
      const url = `${this.$store.getters.appSite}login/${propertyId}`;
      window.open(url, "", "width=900, height=812");
    },
    goToProperty(prop) {
      if (this.isDefaultPropertyTemplate(prop)) {
        this.pendingTemplateEdit = prop;
        this.message = { ...this.confirmEditDefaultTemplate };
        return;
      }
      this.openPropertyEditor(prop);
    },
    goToDefault() {
      const templateId = this.$store.getters.defaultPropertyId;
      this.goToProperty({ propertyId: templateId, key: templateId });
    },
    firestorePropertyUrl(propertyId) {
      const id = propertyId?.propertyId || propertyId?.key || propertyId?.docId || propertyId;
      return `https://console.firebase.google.com/u/0/project/everhostio/firestore/databases/-default-/data/~2Fproperties~2F${encodeURIComponent(id)}`;
    },
    goToFirestoreProperty(prop) {
      const url = this.firestorePropertyUrl(prop);
      const isLocalHost =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";

      // On localhost, ask the Vue dev server to open the OS default browser
      // so Firebase Console does not load inside Cursor's embedded browser.
      if (isLocalHost) {
        fetch(`/__open-external?url=${encodeURIComponent(url)}`).catch(() => {
          window.open(url, "_blank", "noopener,noreferrer");
        });
        return;
      }

      window.open(url, "_blank", "noopener,noreferrer");
    },
    async syncPropertyCardViewFromProperty(prop) {
      const propertyId =
        prop?.propertyId || prop?.key || prop?.docId || prop;
      if (!propertyId || typeof propertyId !== "string") {
        this.$store.commit("setAlert", {
          type: "error",
          text: "Missing property id.",
        });
        return;
      }

      try {
        const propSnap = await firebase
          .firestore()
          .collection("properties")
          .doc(propertyId)
          .get();
        if (!propSnap.exists) {
          this.$store.commit("setAlert", {
            type: "error",
            text: `Property ${propertyId} not found.`,
          });
          return;
        }

        const propData = propSnap.data() || {};
        const viewRef = firebase
          .firestore()
          .collection("propertyCardViews")
          .doc(propertyId);

        if (propData.deletedAt != null) {
          const viewSnap = await viewRef.get();
          if (viewSnap.exists) {
            await viewRef.delete();
            this.$store.commit("setAlert", {
              type: "success",
              text: `Removed card view for deleted property ${propertyId}.`,
            });
          } else {
            this.$store.commit("setAlert", {
              type: "success",
              text: `No card view to remove for deleted property ${propertyId}.`,
            });
          }
          return;
        }

        const payload = {
          propertyId,
          key: propertyId,
          docId: propertyId,
          orgId: propData.orgId ?? null,
          uid: propData.uid ?? null,
          name: propData.name || "",
          avatarImg: propData.avatarImg ?? null,
          city: propData.city ?? null,
          state: propData.state ?? null,
          zip: propData.zip ?? null,
          createdAt: propData.createdAt ?? null,
          updatedAt: propData.updatedAt ?? null,
          publishedAt: propData.publishedAt ?? null,
          showName: propData.showName,
          showAddress: propData.showAddress,
          noguestinfo: propData.noguestinfo,
          isPropertyTemplate: propData.isPropertyTemplate === true,
          syncedAt: firebase.firestore.Timestamp.now(),
          contactInfo: {
            hostPhotoUrl: propData.contactInfo?.hostPhotoUrl ?? null,
          },
        };
        if (propData.mapAddress?.formatted_address) {
          payload.mapAddress = {
            formatted_address: propData.mapAddress.formatted_address,
          };
        }

        // Full replace so removed property fields (e.g. mapAddress) are cleared
        // on the card view. merge:true would leave stale nested fields behind.
        await viewRef.set(payload);

        if (prop && typeof prop === "object") {
          Object.assign(prop, payload);
          if (!payload.mapAddress) {
            this.$delete(prop, "mapAddress");
          }
        }

        this.$store.commit("setAlert", {
          type: "success",
          text: `Synced card view for ${propertyId}.`,
        });
      } catch (error) {
        this.$store.commit("setAlert", {
          type: "error",
          text: `Card view sync failed: ${error.message}`,
        });
      }
    },
    confirmEditDefaultTemplateProceed() {
      if (this.pendingTemplateEdit) {
        this.openPropertyEditor(this.pendingTemplateEdit);
        this.pendingTemplateEdit = null;
      }
      if (this.closeMessage) {
        this.message = { ...this.closeMessage };
      } else {
        this.message = { show: false };
      }
    },
    showAnalytics(propertyId) {
      this.message = {
        show: true,
        page: 0,
        title: "Analytics",
        message: "",
        propertyId: propertyId,
        width: 564,
        slotName: "analytics",
        transition: "dialog-bottom-transition",
        persistent: true,
        actions: [{ type: "spacer" }],
      };
    },
    closeAnalytics() {
      this.message = {};
    },
    showGuestQABuilder(propertyId) {
      const id =
        propertyId?.propertyId || propertyId?.key || propertyId?.docId || propertyId;
      this.message = {
        show: true,
        title: "Guest Q&A",
        message: "",
        propertyId: id,
        width: 760,
        slotName: "guestQA",
        persistent: true,
        actions: [
          { type: "spacer" },
          { label: "Close", clickEmit: "closeGuestQA", plain: true },
        ],
      };
    },
    closeGuestQABuilder() {
      this.message = { show: false };
    },
    showInterview(propertyId) {
      const id =
        propertyId?.propertyId || propertyId?.key || propertyId?.docId || propertyId;
      if (this.$store?.commit) {
        this.$store.commit("toggleTrigger");
      }
      this.message = {
        show: true,
        title: "Set up your digital guide",
        page: 0,
        message: "",
        propertyId: id,
        width: 564,
        slotName: "interview",
        transition: "dialog-bottom-transition",
        persistent: true,
        trigger: this.$store?.getters?.trigger,
        actions: [{ type: "spacer" }],
      };
    },
    closeInterview() {
      this.message = { show: false, page: 0, formData: {} };
      if (typeof this.getProperties === "function") {
        this.getProperties();
      }
    },
    async publishProperty(prop) {
      let publishedAt = Date.now();
      let message = {
        title: "Property Published",
        message: "your property has been published",
      };
      if (prop.publishedAt) {
        publishedAt = null;
        message = {
          title: "Property Unpublished",
          message: "your property has been Unpublished",
        };
      }

      try {
        await this.apiSetProperty(prop.propertyId, { publishedAt });
        this.$set(prop, "publishedAt", publishedAt);
        this.message = {
          width: 300,
          show: true,
          ...message,
        };
      } catch (error) {
        console.error("publishProperty failed", error);
        this.$store.commit("setAlert", {
          type: "error",
          text:
            "Failed to update publish status: " +
            (error.message || "Unknown error"),
        });
      }
    },
    deleteProperty(prop) {
      console.log("deleteProperty", prop);
      this.message = {
        show: true,
        width: 350,
        loading: true,
        persistent: true,
        message: "deleting",
        title: "deleting",
      };
      this.apiMarkPropertyDeletedAt(prop).then((resp) => {
        console.log("deleteProperty response", resp);
        try {
          this.properties = this.properties.filter((el) => el.key != prop.key);
        } catch (e) {
          console.log(e);
        }
        this.message = {
          show: true,
          width: 350,
          message: "Property deleted",
          title: "Property deleted",
          actions: [{ type: "spacer" }, { label: "ok", clickEmit: "cancel" }],
        };
      });
    },
    showTransferProperty(propertyId) {
      const id =
        propertyId?.propertyId || propertyId?.key || propertyId?.docId || propertyId;
      this.message = {
        show: true,
        title: "Transfer Property",
        message:
          "Enter the destination organization ID to transfer this property.",
        propertyId: id,
        width: 550,
        slotName: "transferProp",
        transition: "dialog-bottom-transition",
        persistent: false,
        actions: [{ type: "spacer" }],
      };
    },
    closeTransfer() {
      this.message = { show: false };
    },
    onPropertyTransferred({ propertyId, orgId, orgName }) {
      const destination = orgName || orgId || "another organization";
      const placeholder = {
        key: propertyId,
        propertyId,
        transferredPlaceholder: true,
        transferredToOrgId: orgId,
        transferredToOrgName: destination,
        name: `Transferred to ${destination}`,
      };

      if (Array.isArray(this.properties)) {
        const idx = this.properties.findIndex(
          (prop) => (prop.propertyId || prop.key) === propertyId,
        );
        if (idx !== -1) {
          this.$set(this.properties, idx, placeholder);
          setTimeout(() => {
            if (!Array.isArray(this.properties)) return;
            this.properties = this.properties.filter(
              (prop) =>
                !(
                  prop.transferredPlaceholder &&
                  (prop.propertyId || prop.key) === propertyId
                ),
            );
          }, 4000);
        }
      }

      if (
        this.property &&
        (this.property.propertyId === propertyId ||
          this.propertyId === propertyId)
      ) {
        this.property = placeholder;
      }

      this.closeTransfer();
    },
  },
};
</script>
